import type { ArcadeGameId } from "./games";

/** Static mini-scenes for arcade cabinet cards (pure Canvas). */
export function drawArcadePreview(
  canvas: HTMLCanvasElement,
  id: ArcadeGameId,
): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = 320;
  const h = 140;
  canvas.width = Math.floor(w * dpr);
  canvas.height = Math.floor(h * dpr);
  canvas.style.width = "100%";
  canvas.style.height = "auto";
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  ctx.fillStyle = "#061018";
  ctx.fillRect(0, 0, w, h);

  switch (id) {
    case "note-invaders": {
      // sky + falling orbs + laser line
      for (let i = 0; i < 5; i++) {
        const x = 40 + i * 55;
        const y = 20 + (i % 3) * 22;
        ctx.fillStyle = i % 2 ? "#2fe6b0" : "#7dffd0";
        ctx.beginPath();
        ctx.arc(x, y, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#04140f";
        ctx.font = "700 10px Oxanium, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(["C", "D", "E", "G", "A"][i]!, x, y);
      }
      ctx.strokeStyle = "rgba(240,193,90,0.7)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(20, h - 18);
      ctx.lineTo(w - 20, h - 18);
      ctx.stroke();
      ctx.fillStyle = "#f0c15a";
      ctx.fillRect(w / 2 - 6, h - 34, 12, 16);
      break;
    }
    case "chord-tower": {
      for (let f = 0; f < 5; f++) {
        ctx.fillStyle = f % 2 ? "#178a64" : "#1a9e78";
        ctx.fillRect(w * 0.32, h - 28 - f * 16, w * 0.36, 14);
      }
      ctx.fillStyle = "#f0c15a";
      ctx.fillRect(w * 0.28, h - 14, w * 0.44, 10);
      ctx.fillStyle = "#e8f7f1";
      ctx.font = "700 14px Oxanium, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("C · E · G", w / 2, 28);
      break;
    }
    case "scale-racer": {
      ctx.fillStyle = "#1a2834";
      ctx.fillRect(0, h * 0.55, w, h * 0.45);
      ctx.strokeStyle = "#f0c15a";
      ctx.setLineDash([12, 10]);
      ctx.beginPath();
      ctx.moveTo(0, h * 0.72);
      ctx.lineTo(w, h * 0.72);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#2fe6b0";
      ctx.fillRect(36, h * 0.55 - 16, 44, 20);
      ctx.fillStyle = "#e8f7f1";
      ctx.font = "700 22px Oxanium, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("→ E", w * 0.7, h * 0.38);
      break;
    }
    case "flappy-note": {
      const gap = h * 0.5;
      ctx.fillStyle = "#178a64";
      ctx.fillRect(210, 0, 28, gap - 28);
      ctx.fillRect(210, gap + 28, 28, h - gap - 28);
      ctx.fillStyle = "#f0c15a";
      ctx.beginPath();
      ctx.arc(90, gap, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#8bbbab";
      ctx.font = "11px Oxanium, sans-serif";
      ctx.fillText("pitch = height", 12, 18);
      break;
    }
    case "rhythm-simon": {
      const labels = ["C", "E", "G", "A"];
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = i === 1 ? "#2fe6b0" : "#1a2834";
        ctx.fillRect(28 + i * 70, h * 0.35, 56, 44);
        ctx.fillStyle = "#e8f7f1";
        ctx.font = "700 14px Oxanium, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(labels[i]!, 56 + i * 70, h * 0.35 + 26);
      }
      ctx.fillStyle = "#f0c15a";
      ctx.font = "700 12px Oxanium, sans-serif";
      ctx.fillText("WATCH → REPEAT", w / 2, 22);
      break;
    }
    case "tempo-runner": {
      ctx.fillStyle = "#1a2834";
      ctx.fillRect(0, h * 0.62, w, h * 0.38);
      ctx.fillStyle = "rgba(240,193,90,0.15)";
      ctx.fillRect(60, 0, 90, h);
      ctx.fillStyle = "#e85d5d";
      ctx.fillRect(200, h * 0.62 - 48, 34, 48);
      ctx.fillStyle = "#041018";
      ctx.font = "700 12px Oxanium, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("E", 217, h * 0.62 - 22);
      ctx.fillStyle = "#2fe6b0";
      ctx.fillRect(90, h * 0.62 - 28, 24, 24);
      ctx.fillStyle = "#8bbbab";
      ctx.font = "11px Oxanium, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("match the gate note", 12, 18);
      break;
    }
    case "dual-hands": {
      ctx.fillStyle = "rgba(47,230,176,0.15)";
      ctx.fillRect(0, 0, w / 2, h);
      ctx.fillStyle = "rgba(240,193,90,0.15)";
      ctx.fillRect(w / 2, 0, w / 2, h);
      ctx.fillStyle = "#e8f7f1";
      ctx.font = "700 28px Oxanium, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("C3", w * 0.25, h * 0.5);
      ctx.fillText("E4", w * 0.75, h * 0.5);
      ctx.font = "11px Oxanium, sans-serif";
      ctx.fillStyle = "#8bbbab";
      ctx.fillText("LH + RH together", w / 2, 22);
      break;
    }
  }
}
